import React, { useEffect, useState, useRef } from "react";
import { showToast } from "../common/ToastProvider";
import "./TrackCastedPage.css";

const API_BASE = process.env.REACT_APP_API_URL || "";
const BATCH_SIZE = 20;

const TrackCastedPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [userVote, setUserVote] = useState(null);
  const [highlightedVote, setHighlightedVote] = useState(null);

  // refs
  const candidatesRef = useRef([]);
  const offsetsRef = useRef({});
  const loadingRef = useRef({});
  const seenRef = useRef({});
  const nextCandidateIndexRef = useRef(0);
  const initDoneRef = useRef(false);
  const ignoreScrollRef = useRef(false);

  // keep sync
  useEffect(() => {
    candidatesRef.current = candidates;
  }, [candidates]);

  // initial candidates fetch
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/getCastedVotesInit.php`, {
          credentials: "include",
        });
        const data = await res.json();

        if (!data.success) {
          window.location.href = "/";
          return;
        }

        if (!data.userVote) {
          showToast("You need to cast your vote first!", "warn");
          setTimeout(() => (window.location.href = "/"), 5000);
          return;
        }

        const extended = data.candidates.map((c) => ({
          ...c,
          votes: [],
          offset: 0,
          hasMore: true,
          loading: false,
          totalVotes: c.totalVotes ?? 0,
        }));

        extended.forEach((c) => {
          const id = String(c.id);
          offsetsRef.current[id] = 0;
          loadingRef.current[id] = false;
          seenRef.current[id] = new Set();
        });

        setCandidates(extended);
        setUserVote(data.userVote);
      } catch (err) {
        console.error("init error:", err);
        showToast("Error loading data", "error");
      }
    })();
  }, []);

  // initial sequential batch load
  useEffect(() => {
    if (initDoneRef.current) return;
    if (!candidates || candidates.length === 0) return;

    initDoneRef.current = true;

    const fetchInitialSequential = async () => {
      for (let c of candidatesRef.current) {
        if ((c.votes && c.votes.length > 0) || !c.hasMore) continue;
        await loadMoreVotes(c.id);
      }
    };

    fetchInitialSequential();
  }, [candidates]);

  // scroll listener
  useEffect(() => {
    const onScroll = () => {
      if (ignoreScrollRef.current) return;

      const scrollPos = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.offsetHeight - 800;
      if (scrollPos < threshold) return;

      const list = candidatesRef.current;
      if (!list || list.length === 0) return;

      const total = list.length;
      for (let i = 0; i < total; i++) {
        const idx = nextCandidateIndexRef.current % total;
        nextCandidateIndexRef.current++;
        const c = list[idx];
        if (!c) continue;
        const cid = String(c.id);
        if (c.hasMore && !loadingRef.current[cid]) {
          loadMoreVotes(c.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // core loader
  const loadMoreVotes = async (cid, userSymbol = null) => {
    const cidStr = String(cid);
    if (loadingRef.current[cidStr]) return 0;

    const cand = candidatesRef.current.find((x) => String(x.id) === cidStr);
    if (!cand || !cand.hasMore) return 0;

    loadingRef.current[cidStr] = true;
    setCandidates((prev) =>
      prev.map((c) => (String(c.id) === cidStr ? { ...c, loading: true } : c))
    );

    const offset = offsetsRef.current[cidStr] ?? 0;
    let url = `${API_BASE}/getCastedVotes.php?candidate_id=${cidStr}&offset=${offset}&limit=${BATCH_SIZE}`;
    if (userSymbol) url += `&userSymbol=${userSymbol}`;

    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (!data || data.success !== true) {
        loadingRef.current[cidStr] = false;
        setCandidates((prev) =>
          prev.map((c) =>
            String(c.id) === cidStr ? { ...c, loading: false } : c
          )
        );
        return 0;
      }

      // dedupe
      const seen = seenRef.current[cidStr] || new Set();
      const incoming = Array.isArray(data.votes) ? data.votes : [];
      const newItems = [];
      for (let v of incoming) {
        const vid = String(v.id);
        if (!seen.has(vid)) {
          seen.add(vid);
          newItems.push(v);
        }
      }
      seenRef.current[cidStr] = seen;

      // update offsets
      offsetsRef.current[cidStr] =
        (offsetsRef.current[cidStr] || 0) + newItems.length;
      const totalVotes = Number(data.totalVotes) || cand.totalVotes || 0;
      const newOffsetCount = offsetsRef.current[cidStr] || 0;
      const newHasMore = newOffsetCount < totalVotes;

      setCandidates((prev) =>
        prev.map((c) => {
          if (String(c.id) !== cidStr) return c;
          return {
            ...c,
            votes: [...(c.votes || []), ...newItems],
            offset: newOffsetCount,
            hasMore: newHasMore,
            loading: false,
            totalVotes,
          };
        })
      );

      loadingRef.current[cidStr] = false;
      return newItems.length;
    } catch (err) {
      console.error("loadMoreVotes error:", err);
      loadingRef.current[cidStr] = false;
      setCandidates((prev) =>
        prev.map((c) =>
          String(c.id) === cidStr ? { ...c, loading: false } : c
        )
      );
      showToast("Error loading votes", "warn");
      return 0;
    }
  };

  // search button logic
  const handleSearch = async () => {
    if (!userVote) return;
    setHighlightedVote(userVote);

    const cidStr = String(userVote.candidate_id);
    const symbol = userVote.symbol_code;

    let candidate = candidatesRef.current.find((c) => String(c.id) === cidStr);
    if (!candidate) return;

    const alreadyLoaded = (candidate.votes || []).some(
      (v) => v.symbol_code === symbol
    );

    ignoreScrollRef.current = true;

    if (!alreadyLoaded) {
      // reset only when needed
      setCandidates((prev) =>
        prev.map((c) =>
          String(c.id) === cidStr
            ? { ...c, votes: [], offset: 0, hasMore: true, loading: true }
            : c
        )
      );
      offsetsRef.current[cidStr] = 0;
      seenRef.current[cidStr] = new Set();

      await loadMoreVotes(candidate.id, symbol);
    }

    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-sym="${cidStr}__${symbol}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      showToast("Your vote is highlighted", "success");

      // delay re-enable to avoid glitch
      setTimeout(() => {
        ignoreScrollRef.current = false;
      }, 800);
    });
  };

  return (
    <div className="track-vote-container">
      <div className="header-banner">TRACK MY CASTED VOTE</div>
      <div className="sticky-container">
        
        <div className="sticky-container1">
          <div className="search-container">
          <div className="search-text">CLICK ON SEARCH ICON</div>
          <div className="search-icon-group">
            <div className="icon-with-speaker">
              <button
                className="search-icon-button p-0"
                onClick={handleSearch}
                disabled={!userVote}
              >
                <img src="/images/search.jpg" alt="Search" width="30" />
              </button>
              <img src="/images/speaker.webp" alt="Speaker" width="30" />
            </div>

            <div className="search-label">SEARCH ICON</div>
          </div>
        </div>
        </div>
        
      </div>

      <div className="candidates-grid">
        {candidates.map((c) => {
          const cidStr = String(c.id);
          return (
            <div key={c.id} className="candidate-box">
              <div className="middle-container ">
                <img
                  src={`${API_BASE}/${c.party_photo}`}
                  alt="party symbol"
                  className="party-symbol"
                />
                <div className="total-votes-label">VOTES</div>
                <div className="vote-number">{c.totalVotes}</div>
                <div className="symbol-list-header">SYMBOL CODE</div>
              </div>

              <div className="symbol-list">
                {c.votes && c.votes.length === 0 && !c.loading && (
                  <p className="no-votes">No votes loaded yet</p>
                )}

                {c.votes &&
                  c.votes.map((sym, idx) => {
                    const isHighlighted =
                      highlightedVote &&
                      highlightedVote.symbol_code === sym.symbol_code &&
                      String(highlightedVote.candidate_id) === cidStr;

                    return (
                      <p
                        key={sym.id || idx}
                        data-sym={`${cidStr}__${sym.symbol_code}`}
                        className={`symbol-code ${
                          isHighlighted ? "highlighted" : ""
                        }`}
                      >
                        {sym.symbol_code}
                      </p>
                    );
                  })}

                {/* skeleton: show only when no votes yet */}
                {c.loading &&
                  c.votes.length === 0 &&
                  [...Array(5)].map((_, i) => (
                    <div
                      key={`skeleton-${cidStr}-${i}`}
                      className="skeleton"
                    ></div>
                  ))}

                {!c.hasMore && c.votes && c.votes.length > 0 && (
                  <p className="end-msg"></p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrackCastedPage;
